/**
 * 本番モード判定ユーティリティ
 *
 * URLパラメータまたは環境変数で本番モードを判定します。
 * 本番モードではデバッグUIが非表示になります。
 */

import { useMemo } from 'react';

/**
 * 本番モードかどうかを判定
 *
 * 判定条件:
 * 1. URLパラメータ: ?prod=1 または ?productionMode=1
 * 2. 環境変数: VITE_PRODUCTION_MODE=1（または true）
 *
 * 環境変数側は最終監査 §2-1-4（prodガード穴）対応: 本番ビルドで
 * `VITE_PRODUCTION_MODE=1` を注入すれば、URLパラメータなしの素の URL でも
 * 本番モードになる。'true' も後方互換のため受理する。未設定時は従来どおり
 * URLパラメータのみで判定される。
 */
export function isProductionMode(): boolean {
  if (typeof window !== 'undefined') {
    const params = new URLSearchParams(window.location.search);
    if (params.get('prod') === '1' || params.get('productionMode') === '1') {
      return true;
    }
  }
  const flag = import.meta.env.VITE_PRODUCTION_MODE;
  return flag === '1' || flag === 'true';
}

/**
 * 本番モード判定フック
 *
 * Reactコンポーネント内で使用し、レンダリング間でメモ化されます。
 */
export function useProductionMode(): boolean {
  return useMemo(() => isProductionMode(), []);
}

/**
 * プレビューモードかどうかを判定
 *
 * Studio からの埋め込みプレビュー時に true
 */
export function isPreviewMode(): boolean {
  if (typeof window !== 'undefined') {
    const params = new URLSearchParams(window.location.search);
    return params.get('preview') === '1';
  }
  return false;
}

/**
 * プレビューモード判定フック
 */
export function usePreviewMode(): boolean {
  return useMemo(() => isPreviewMode(), []);
}

/**
 * モックモードかどうかを判定
 *
 * API接続なしでローカルデータで動作する時に true
 */
export function isMockMode(): boolean {
  if (typeof window !== 'undefined') {
    const params = new URLSearchParams(window.location.search);
    return params.get('mode') === 'mock';
  }
  return false;
}

/**
 * モックモード判定フック
 */
export function useMockMode(): boolean {
  return useMemo(() => isMockMode(), []);
}

/**
 * 開発モードかどうかを判定
 *
 * 本番モードでもプレビューモードでもない場合に true
 */
export function isDevelopmentMode(): boolean {
  return !isProductionMode() && !isPreviewMode();
}

/**
 * 開発モード判定フック
 */
export function useDevelopmentMode(): boolean {
  return useMemo(() => isDevelopmentMode(), []);
}
